import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const TermsOfService = () => {
  useSEO({
    title: 'Terms of Service',
    description: 'Read the Terms of Service for Family Wishlist. Learn about our age restrictions (13+), parenting/guardian controls, user conduct guidelines, and content moderation rules.',
    keywords: 'family wishlist terms, terms of service, child protection rules, content moderation',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      'name': 'Family Wishlist Terms of Service',
      'description': 'Guidelines and terms governing the use of Family Wishlist, including age limits and content moderation rules.',
      'url': `${window.location.origin}/terms-of-service`
    }
  });

  return (
    <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xmas-green hover:text-xmas-red font-bold transition-colors"
        >
          <ArrowLeft size={20} /> Home
        </Link>
      </div>
      <h1 className="text-4xl font-bold text-xmas-red mb-8 text-center">Terms of Service</h1>

      <div className="prose prose-slate max-w-none space-y-6 text-gray-700">
        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">1. Acceptance of Terms</h2>
          <p>
            By accessing or using the Family Wishlist service, you agree to be bound by these Terms of Service. If you do not agree to these terms, you must not use or access the website or its services.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">2. Age Limit & Child Protection</h2>
          <div className="space-y-4">
            <p>
              Our website and services are intended solely for individuals who are 13 years of age or older.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>No Registration Under 13:</strong> Children under the age of 13 are strictly prohibited from creating accounts, registering, or submitting any personal information directly on this website.
              </li>
              <li>
                <strong>Parental/Guardian Management:</strong> Parents or legal guardians may create and manage wishlists or wishlist items on behalf of their children under 13. All data related to child-focused gifts or lists must be entered and moderated exclusively by the child’s authorised parent or guardian using the parent/guardian’s account.
              </li>
              <li>
                <strong>Account Termination:</strong> If we learn that we have collected personal information from a child under 13 without verified parental consent, we will delete that information and terminate the associated account as quickly as possible.
              </li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">3. User Conduct & Content Moderation</h2>
          <p>
            You are solely responsible for the content you post on your wishlist. To ensure a safe and family-friendly environment, you agree not to submit:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Any content that is offensive, abusive, defamatory, or violates community standards.</li>
            <li>Weapons, hate speech, crime/violence, or sexually explicit material.</li>
            <li>Any links using malicious or non-standard protocols (all links must begin with <code>http://</code> or <code>https://</code>).</li>
          </ul>
          <p className="mt-4">
            Our platform runs automated daily scans to identify and remove inappropriate content. The website administrator reserves the right, in their sole discretion, to remove any wishlist items, groups, or user accounts that violate these guidelines without prior notice.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">4. Limitation of Liability & Assumption of Risk</h2>
          <div className="space-y-4">
            <p>
              Please understand that Family Wishlist is a free service provided "as is" and "as available".
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>No Absolute Security:</strong> While standard security protocols are implemented to protect your data, no internet transmission or database storage can be guaranteed 100% secure.
              </li>
              <li>
                <strong>Administrator Access:</strong> A single system administrator has technical read/write visibility to all database records to support maintenance and technical troubleshooting.
              </li>
              <li>
                <strong>Release of Liability:</strong> By using this platform, you explicitly agree that you submit all information at your own risk. You hereby release the system administrator, platform creators, and associated affiliates from any liability, loss, or damages arising out of unauthorized database access, technical errors, or account compromises.
              </li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">5. Termination and Modifications</h2>
          <p>
            We reserve the right to suspend or terminate your access to the service at any time, with or without cause or notice. We may also revise these Terms of Service from time to time. Your continued use of the platform after updates are made constitutes acceptance of the new terms.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">6. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service or wish to request the deletion of your account and data, please contact the website administrator at:
          </p>
          <p className="font-bold mt-2">
            <a href="mailto:privacy@family-wishlist.com" className="text-xmas-red hover:underline">
              privacy@family-wishlist.com
            </a>
          </p>
        </section>

        <section className="pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Last updated: July 2026
          </p>
        </section>
      </div>
    </div>
  );
};

export default TermsOfService;
